import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileUpload } from "@cloudscape-design/components";
import { fetchTemplate, createTemplateApi, listCampaignsApi, getAllTemplates, presign, addKbFileToTemplate, deleteKbFileFromTemplate } from '../api/api';
import axios from 'axios';
import ConfirmationModal from '../components/ConfirmationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { FaTimes } from 'react-icons/fa';

const emptyQuestion = {
  questionName: '',
  description: '',
  type: 'Quality',
  weight: '',
  expected: '',
  // v2.0 fields
  section: '',
  assessment: '',
  responseType: 'yes_no',
  qaWeighting: '',
  complianceWeighting: '',
  isCriticalFail: false,
  success: 'yes',  // Default to 'yes' - positive answer awards points
  sortOrder: 999
};

const TemplateEditor = ({ setAlert }) => {
  const navigate = useNavigate();
  const { name } = useParams();
  const [templates, setTemplates] = useState()
  const [templateName, setTemplateName] = useState('');
  const [campaign, setCampaign] = useState('');
  const [questions, setQuestions] = useState([ { ...emptyQuestion } ]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [inputError, setInputError] = useState([false]);
  const [saveCurrent, setSaveCurrent] = useState(false);
  const [useYesNoScoring, setUseYesNoScoring] = useState(true); // v2.0 by default
  const [selectedLanguage, setSelectedLanguage] = useState('en-ZA');
  const [upload, setUpload] = useState([]);
  const [kbFiles, setKbFiles] = useState([]);
  const [deleteKbInfo, setDeleteKbInfo] = useState({ open: false, fileId: null });

  const handleFileAdd = ({ detail }) => {
    const newFiles = detail.value;
    setUpload(newFiles);
  };

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const res = await listCampaignsApi();
        setCampaigns(res.campaigns || []);
        const res2 = await getAllTemplates();
        setTemplates(res2.templates)
      } catch (err) {
        console.error('Failed to load campaigns', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaigns.' });
      }
    };
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (!name) {
      setLoading(false);
      setDirty(false);
      return;
    }
    const load = async () => {
      try {
        const res = await fetchTemplate(name);
        setTemplateName(res.templateName);
        const loadedQuestions = res.questions || [];
        // Sort questions by sortOrder to ensure consistent display
        loadedQuestions.sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));

        // Migrate old 'weight' field to 'qaWeighting' if needed
        const migratedQuestions = loadedQuestions.map(q => ({
          ...q,
          qaWeighting: q.qaWeighting || q.weight || '',
        }));

        setQuestions(migratedQuestions);

        // Load language if available
        if (res.language) {
          setSelectedLanguage(res.language);
        }

        // Load KB files if available
        if (res.kbFiles) {
          setKbFiles(res.kbFiles);
        }

        // Auto-detect scoring type from existing questions
        if (loadedQuestions.length > 0) {
          const hasYesNoQuestions = loadedQuestions.some(q => q.responseType === 'yes_no');
          const hasScoredQuestions = loadedQuestions.some(q => q.responseType === 'scored');

          if (hasYesNoQuestions) {
            setUseYesNoScoring(true);
          } else if (hasScoredQuestions) {
            setUseYesNoScoring(false);
          }
        }
      } catch (err) {
        console.error('Failed to fetch scorecard', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load scorecard.' });
      } finally {
        setLoading(false);
        setDirty(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const addQuestion = () => {
    const newQuestion = {
      ...emptyQuestion,
      responseType: useYesNoScoring ? 'yes_no' : 'scored'
    };
    setQuestions(q => [...q, newQuestion]);
    setDirty(true);
  };
  const updateQuestion = (index, key, value) => { setQuestions(q => q.map((item,i)=> i===index ? { ...item, [key]: value } : item)); setDirty(true); };
  const removeQuestion = (index) => { setQuestions(q => q.filter((_,i)=>i!==index)); setDirty(true); };

  // Calculate total QA weighting for yes/no scoring (only Quality questions)
  const calculateTotalQaWeighting = () => {
    if (!useYesNoScoring) return null;
    return questions.reduce((sum, q) => {
      // Only include Quality questions in the total (Compliance questions don't have weighting)
      if (q.type === 'Quality') {
        const weight = parseFloat(q.qaWeighting || q.weight || 0);
        return sum + weight;
      }
      return sum;
    }, 0);
  };

  // Calculate total Compliance weighting for yes/no scoring (only Quality questions)
  const calculateTotalComplianceWeighting = () => {
    if (!useYesNoScoring) return null;
    return questions.reduce((sum, q) => {
      // Only include Quality questions in the total (Compliance questions don't have weighting)
      if (q.type === 'Quality') {
        const weight = parseFloat(q.complianceWeighting || 0);
        return sum + weight;
      }
      return sum;
    }, 0);
  };

  const handleSave = async () => {
    // Validate QA weighting totals 100 for yes/no scoring
    if (useYesNoScoring) {
      const qaTotal = calculateTotalQaWeighting();
      if (Math.abs(qaTotal - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `QA Weighting must total exactly 100%. Current total: ${qaTotal.toFixed(2)}%`]);
        setSaveConfirm(false);
        return;
      }

      // Validate Compliance weighting totals 100 for yes/no scoring
      const complianceTotal = calculateTotalComplianceWeighting();
      if (Math.abs(complianceTotal - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `Compliance Weighting must total exactly 100%. Current total: ${complianceTotal.toFixed(2)}%`]);
        setSaveConfirm(false);
        return;
      }
    }

    if (dirty && name && templateName.trim() === name.trim()) {
      setInputError([true, `Please provide a different version name before saving.`])
      setSaveConfirm(false);
      return;
    }
    for (let i =0; i<= templates.length -1 ; i++) {
      if (templates[i].templateName === templateName) {
        setInputError([true, `Template name '${templates[i].templateName}' already exists.`])
        setSaveConfirm(false);
        return;
      }      
    }

    setSaving(true);
    try {
      const payload = name
        ? {
            isVersionCreation: true,
            newTemplateName: templateName,
            originalTemplateName: name,
            campaign: campaign || undefined,
            language: selectedLanguage,
            questions,
          }
        : { templateName, campaign: campaign || undefined, language: selectedLanguage, questions };
        console.log("PAYLOAD: ", payload)
      await createTemplateApi(payload);

      // Upload knowledge base files for this template and register in KB management
      if (upload.length !== 0) {
        for (let i = 0; i < upload.length; i++) {
          const filename = upload[i].name;
          const s3Up = await presign(filename, 'scorecard-kb', templateName);
          await axios.put(s3Up.url, upload[i]);
          console.log(`S3 presign URL for ${filename}: ${s3Up}`);
          // Register the file in KB management system linked to this template
          await addKbFileToTemplate(templateName, filename, s3Up.key || filename);
        }
        setUpload([]);
      }

      setAlert?.({ variant: 'success', heading: 'Scorecard Saved', text: 'Scorecard saved successfully.' });
      setDirty(false);
      navigate('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
    }
  };

  // Delete KB file handler
  const handleDeleteKbFile = async () => {
    if (!deleteKbInfo.fileId || !name) return;
    try {
      await deleteKbFileFromTemplate(name, deleteKbInfo.fileId);
      setKbFiles(files => files.filter(f => f !== deleteKbInfo.fileId));
      setAlert?.({ variant: 'success', heading: 'File Deleted', text: 'Knowledge base file deleted successfully.' });
    } catch (err) {
      console.error('Failed to delete KB file', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to delete knowledge base file.' });
    } finally {
      setDeleteKbInfo({ open: false, fileId: null });
    }
  };

  const handleSaveCurrent = async () => {
    // Validate QA weighting totals 100 for yes/no scoring
    if (useYesNoScoring) {
      const total = calculateTotalQaWeighting();
      if (Math.abs(total - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `QA Weighting must total exactly 100%. Current total: ${total.toFixed(2)}%`]);
        return;
      }
    }

    if (name !== templateName) {
      setInputError([true, `Your scorecard name does not match the question set you are editing.
        Please enter the correct scorecard name.`])
      return;
    }

    setSaving(true);
    try {
      const payload = {
            currentTemplateName: templateName,
            campaign: campaign || undefined,
            language: selectedLanguage,
            questions,
          };
        console.log("PAYLOAD Current: ", payload)
      await createTemplateApi(payload);

      // Upload knowledge base files for this template and register in KB management
      if (upload.length !== 0) {
        for (let i = 0; i < upload.length; i++) {
          const filename = upload[i].name;
          const s3Up = await presign(filename, 'scorecard-kb', templateName);
          await axios.put(s3Up.url, upload[i]);
          console.log(`S3 presign URL for ${filename}: ${s3Up}`);
          // Register the file in KB management system linked to this template
          await addKbFileToTemplate(templateName, filename, s3Up.key || filename);
        }
        setUpload([]);
      }

      setAlert?.({ variant: 'success', heading: 'Scorecard Updated', text: 'Scorecard updated successfully.' });
      setDirty(false);
      navigate('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-20 text-gray-500">Loading...</div>;
  }


  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6">

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{name ? 'Edit Scorecard' : 'New Scorecard'}</h1>
      </div>
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Scorecard Name</label>
          <input
          className={`input-field ${inputError ? "input-error" : ""}`}
          autoFocus={inputError[0]}
          value={templateName}
          onChange={e=>{setTemplateName(e.target.value); setDirty(true);}} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign (optional)</label>
          <select value={campaign} onChange={e=>{setCampaign(e.target.value); setDirty(true);}} className="select-field">
            <option value="">None</option>
            {campaigns.map(c => (
              <option key={c.campaignName} value={c.campaignName}>{c.campaignName}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                checked={selectedLanguage === 'en-ZA'}
                onChange={() => {setSelectedLanguage('en-ZA'); setDirty(true);}}
                className="mr-2"
              />
              <span>en-ZA</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={selectedLanguage === 'en-US'}
                onChange={() => {setSelectedLanguage('en-US'); setDirty(true);}}
                className="mr-2"
              />
              <span>en-US</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={selectedLanguage === 'en-GB'}
                onChange={() => {setSelectedLanguage('en-GB'); setDirty(true);}}
                className="mr-2"
              />
              <span>en-GB</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={selectedLanguage === 'zu-ZA'}
                onChange={() => {setSelectedLanguage('zu-ZA'); setDirty(true);}}
                className="mr-2"
              />
              <span>zu-ZA (Zulu - South Africa)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={selectedLanguage === 'auto-detect'}
                onChange={() => {setSelectedLanguage('auto-detect'); setDirty(true);}}
                className="mr-2"
              />
              <span>Auto Detect</span>
            </label>
          </div>
        </div>

        {/* Scoring Type Toggle */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Scoring System</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                checked={useYesNoScoring}
                onChange={() => {
                  setUseYesNoScoring(true);
                  setDirty(true);
                  // Update all questions to yes_no
                  setQuestions(q => q.map(question => ({
                    ...question,
                    responseType: 'yes_no'
                  })));
                }}
                className="mr-2"
              />
              <span className="font-medium">Yes/No Scoring (v2.0)</span>
              <span className="ml-2 text-xs text-gray-500">- Questions get full weight if "Yes", zero if "No"</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!useYesNoScoring}
                onChange={() => {
                  setUseYesNoScoring(false);
                  setDirty(true);
                  // Update all questions to scored
                  setQuestions(q => q.map(question => ({
                    ...question,
                    responseType: 'scored'
                  })));
                }}
                className="mr-2"
              />
              <span className="font-medium">Numeric Scoring (v1.0 - Legacy)</span>
              <span className="ml-2 text-xs text-gray-500">- Questions scored 0-100</span>
            </label>
          </div>
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
            <strong>Current:</strong> {useYesNoScoring ? (
              <span>Yes/No scoring with Quality and Compliance weighting</span>
            ) : (
              <span>Numeric scoring (0-100) with single weight value</span>
            )}
          </div>
        </div>
        <div className="space-y-6">
          {questions.map((q, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <span className="font-medium text-gray-700">Question {idx + 1}</span>
                <button onClick={() => removeQuestion(idx)} className="text-red-600 hover:text-red-800 text-sm">
                  Remove
                </button>
              </div>

              {/* Row 1: Question Name, Section, Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Question Name</label>
                  <input
                    value={q.questionName}
                    onChange={e=>updateQuestion(idx,'questionName',e.target.value)}
                    className="input-field"
                    placeholder="e.g., greeting_check"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Section</label>
                  <input
                    type="text"
                    value={q.section || ''}
                    onChange={e=>updateQuestion(idx,'section',e.target.value)}
                    className="input-field"
                    placeholder="e.g., Introduction, Pitch, Close"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter custom section name</p>
                </div>                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select
                    value={q.type}
                    onChange={e=>updateQuestion(idx,'type',e.target.value)}
                    className="select-field"
                  >
                    <option value="Quality">Quality</option>
                    <option value="Compliance">Compliance</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Quality: Scored | Compliance: Pass/Fail checks</p>
                </div>
              </div>

              {/* Row 2: Assessment Question */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assessment Question</label>
                <textarea
                  rows="2"
                  value={q.assessment || q.description}
                  onChange={e=>{
                    updateQuestion(idx,'assessment',e.target.value);
                    updateQuestion(idx,'description',e.target.value);
                  }}
                  className="input-field w-full"
                  placeholder="Did the agent greet the customer correctly?"
                />
              </div>

              {/* Row 3: Expected Criteria */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expected Criteria</label>
                <textarea
                  rows="2"
                  value={q.expected || ''}
                  onChange={e=>updateQuestion(idx,'expected',e.target.value)}
                  className="input-field w-full"
                  placeholder="Agent should greet within 10 seconds..."
                />
              </div>

              {/* Row 4: Weighting (conditional based on scoring type) */}
              {useYesNoScoring ? (
                // Yes/No Scoring Fields
                <div className={`grid grid-cols-1 md:grid-cols-${q.type === 'Compliance' ? '2' : '4'} gap-4`}>
                  {/* Only show weighting fields for Quality questions */}
                  {q.type === 'Quality' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">QA Weighting (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={q.qaWeighting || q.weight || ''}
                          onChange={e=>updateQuestion(idx,'qaWeighting',e.target.value)}
                          onWheel={e => { e.target.blur(); e.stopPropagation(); setTimeout(() => e.target.focus(), 0); }}
                          className="input-field"
                          placeholder="2.5"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Compliance Weighting (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={q.complianceWeighting || ''}
                          onChange={e=>updateQuestion(idx,'complianceWeighting',e.target.value)}
                          onWheel={e => { e.target.blur(); e.stopPropagation(); setTimeout(() => e.target.focus(), 0); }}
                          className="input-field"
                          placeholder="5.56"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      <input
                        type="checkbox"
                        checked={q.isCriticalFail || false}
                        onChange={e=>updateQuestion(idx,'isCriticalFail',e.target.checked)}
                        className="mr-2"
                      />
                      Critical Fail
                    </label>
                    <div className="text-xs text-gray-500 mt-1">
                      {q.isCriticalFail ? `⚠️ Auto-fails call if "${q.success === 'no' ? 'Yes' : 'No'}"` : 'Not critical'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Success Answer
                    </label>
                    <select
                      value={q.success || 'yes'}
                      onChange={e => updateQuestion(idx, 'success', e.target.value)}
                      className="input-field"
                    >
                      <option value="yes">Yes awards points</option>
                      <option value="no">No awards points</option>
                    </select>
                    <div className="text-xs text-gray-500 mt-1">
                      {q.success === 'no' ? '✓ "No" = Success' : '✓ "Yes" = Success'}
                    </div>
                  </div>
                </div>
              ) : (
                // Legacy Scored Fields
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Weight (0-100)</label>
                  <input
                    type="number"
                    value={q.weight || ''}
                    onChange={e=>updateQuestion(idx,'weight',e.target.value)}
                    className="input-field"
                    placeholder="Weight for scored questions"
                  />
                  <p className="text-xs text-gray-500 mt-1">Question will be scored 0-100, then weighted by this value</p>
                </div>
              )}
            </div>
          ))}

          {/* QA Weighting Total Indicator */}
          {useYesNoScoring && (
            <div className={`p-4 rounded-md mb-4 ${
              Math.abs(calculateTotalQaWeighting() - 100) < 0.01
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Total QA Weighting:
                </span>
                <span className={`text-lg font-bold ${
                  Math.abs(calculateTotalQaWeighting() - 100) < 0.01
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {calculateTotalQaWeighting().toFixed(2)}%
                </span>
              </div>
              {Math.abs(calculateTotalQaWeighting() - 100) < 0.01 ? (
                <p className="text-xs text-green-600 mt-1">✓ Total is exactly 100%</p>
              ) : (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Must equal 100% to save (currently {(calculateTotalQaWeighting() - 100).toFixed(2)}% {calculateTotalQaWeighting() > 100 ? 'over' : 'under'})
                </p>
              )}
            </div>
          )}

          {/* Compliance Weighting Total Indicator */}
          {useYesNoScoring && (
            <div className={`p-4 rounded-md mb-4 ${
              Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Total Compliance Weighting:
                </span>
                <span className={`text-lg font-bold ${
                  Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {calculateTotalComplianceWeighting().toFixed(2)}%
                </span>
              </div>
              {Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01 ? (
                <p className="text-xs text-green-600 mt-1">✓ Total is exactly 100%</p>
              ) : (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Must equal 100% to save (currently {(calculateTotalComplianceWeighting() - 100).toFixed(2)}% {calculateTotalComplianceWeighting() > 100 ? 'over' : 'under'})
                </p>
              )}
            </div>
          )}

          <button onClick={addQuestion} className="btn-outline w-full">+ Add Question</button>
        </div>
        <div className="mt-4 border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Knowledge Base Files
          </label>
          {/* Display attached KB files */}
          {name && kbFiles.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Attached files:</p>
              <ul className="space-y-2">
                {kbFiles.map((fileId) => (
                  <li key={fileId} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                    <span className="text-sm text-gray-700">{fileId}</span>
                    <button
                      type="button"
                      onClick={() => setDeleteKbInfo({ open: true, fileId })}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete file"
                    >
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {name && kbFiles.length === 0 && (
            <p className="text-xs text-gray-500 mb-2">No knowledge base files attached.</p>
          )}
          {/* Upload new files */}
          <p className="text-xs text-gray-500 mb-2">Upload new files:</p>
          <FileUpload
            onChange={handleFileAdd}
            value={upload}
            i18nStrings={{
              uploadButtonText: e => e ? "Upload files" : "Upload file",
              dropzoneText: e => e ? "Drop files to upload" : "Drop file to upload",
              removeFileAriaLabel: e => `Remove file ${e + 1}`,
              limitShowFewer: "Show fewer files",
              limitShowMore: "Show more files",
              errorIconAriaLabel: "Error",
              warningIconAriaLabel: "Warning"
            }}
            multiple
            showFileLastModified
            showFileSize
            showFileThumbnail
            tokenLimit={3}
          />
        </div>
        {inputError[0] && (
          <div className="custom-alert">
            <span>{inputError[1]}</span>
            <button className="dismiss-btn" onClick={() => setInputError([false])}>
              <FaTimes />
            </button>
          </div>
        )}         
        <div className="flex justify-end">
          <button onClick={() => setSaveConfirm(true)} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save New Version'}
          </button>
          {name && (
            <button style={{marginLeft: '15px'}} onClick={() => setSaveCurrent(true)} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Overwrite Current Version'}
            </button>      
          )}          
        </div>

      </div>
    </div>
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${templateName || 'this scorecard'}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    <ConfirmationModal
      open={saveCurrent}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to the current scorecard ${name || ''}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveCurrent(false)}
      onConfirm={handleSaveCurrent}
    />
    <DeleteConfirmationModal
      open={deleteKbInfo.open}
      itemName={deleteKbInfo.fileId || ''}
      onClose={() => setDeleteKbInfo({ open: false, fileId: null })}
      onConfirm={handleDeleteKbFile}
    />
    </>
  );
};

export default TemplateEditor;
